import React, { useState, useEffect } from "react";
import ParticipantsSummary from "./ParticipantsSummary";
import AdditionalInfoSummary from "./AdditionalInfoSummary";
import Actions from "./Actions";
import "../../css/Summary.css";
import SummaryClauses from "./SummaryClauses";
import { summaryClauses } from "../../Utils/summaryClauses.js";
import CostSummary from "./CostSummary.js";

const Summary = ({ data, onConfirm, onBack }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const currentClauses = data.isTouristEvent
    ? summaryClauses.touristEvent
    : summaryClauses.nonTouristEvent;

  const requiresPostalAgreement = data.config.isTouristEvent;

  const initialDeclarations = currentClauses.reduce((acc, clause) => {
    acc[clause.id] = false;
    return acc;
  }, {});

  const [declarations, setDeclarations] = useState(initialDeclarations);
  const [showPopup, setShowPopup] = useState(false);
  const [postalAddress, setPostalAddress] = useState({});
  const [savedAddress, setSavedAddress] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const withNNW = data.config.nnwRequired || data.nnwInsurance;
  const withKR = withNNW && data.krInsurance;


  const handleDeclarationChange = (id, value) => {
    setDeclarations((prev) => {
      const newDeclarations = { ...prev, [id]: value };

      if (id === "emailAgreementConsent" && value) {
        setSavedAddress(""); 
      }

      return newDeclarations;
    });
  };


  const areRequiredClausesChecked = Object.entries(declarations)
    .filter(([id]) => currentClauses.some((clause) => clause.id === id && clause.required))
    .every(([, value]) => value);

  const isEmailAgreementChecked = declarations["emailAgreementConsent"];

  const handleConfirmClick = () => {
    if (!areRequiredClausesChecked) {
      setErrorMessage("Aby zakończyć rezerwację, musisz zaakceptować wszystkie wymagane zgody.");
      return;
    }

    if (requiresPostalAgreement && !isEmailAgreementChecked && !savedAddress) {
      setShowPopup(true);
    } else {
      setErrorMessage("");
      onConfirm(declarations, savedAddress);
    }
  };

  const handleSaveAddress = () => {
    setSavedAddress(postalAddress);
    setShowPopup(false);
  };

  const canSendHome =
  postalAddress.street?.trim() &&
  postalAddress.houseNumber?.trim() &&
  postalAddress.postalCode?.trim() &&
  postalAddress.city?.trim() &&
  postalAddress.country?.trim();

  return (
    <div className="summary-container">
      <h2>Podsumowanie rejestracji</h2>


      <ParticipantsSummary participants={data.participants} showInsurance={withNNW} krInsurance={withKR} />
      <CostSummary data={data} withNNW={withNNW} withKR={withKR} />


      <AdditionalInfoSummary additionalInfo={data.additionalInfo}/>

      <SummaryClauses
        isTouristEvent={data.isTouristEvent}
        declarations={declarations}
        onChange={handleDeclarationChange}
      />

      {errorMessage && <p className="error-message">{errorMessage}</p>}

      {savedAddress && requiresPostalAgreement && (
        <p className="saved-address-info">
          Umowa zostanie wysłana pocztą na adres: <strong>{savedAddress}</strong>
        </p>
      )}

      <Actions onConfirm={handleConfirmClick} onBack={onBack} disabled={!areRequiredClausesChecked} />

      {showPopup && requiresPostalAgreement && (
  <>
    <div className="popup-overlay"></div>
    <div className="address-popup">
      <h3>🌳 Chroń razem z nami środowisko! 🌳</h3>
      <p>
        Wyraź zgodę na wysłanie umowy na adres e-mail.
        <br />
        Jeśli jednak chcesz dostać umowę w formie papierowej, listem poleconym, wpisz swój adres poniżej.
      </p>

      <div className="field-row">
        <label htmlFor="street">Ulica<span className="required">*</span>:</label>
        <input
          id="street"
          type="text"
          value={postalAddress.street || ""}
          onChange={(e) =>
            setPostalAddress((prev) => ({ ...prev, street: e.target.value }))
          }
        />
      </div>

      <div className="field-row">
        <label htmlFor="number">Nr domu<span className="required">*</span>:</label>
        <input
          id="number"
          type="text"
          value={postalAddress.number || ""}
          onChange={(e) =>
            setPostalAddress((prev) => ({ ...prev, number: e.target.value }))
          }
        />
      </div>

      <div className="field-row">
        <label htmlFor="apartment">Nr mieszkania:</label>
        <input
          id="apartment"
          type="text"
          value={postalAddress.apartment || ""}
          onChange={(e) =>
            setPostalAddress((prev) => ({ ...prev, apartment: e.target.value }))
          }
        />
      </div>

      <div className="field-row">
        <label htmlFor="zip">Kod pocztowy<span className="required">*</span>:</label>
        <input
          id="zip"
          type="text"
          value={postalAddress.zip || ""}
          onChange={(e) =>
            setPostalAddress((prev) => ({ ...prev, zip: e.target.value }))
          }
        />
      </div>

      <div className="field-row">
        <label htmlFor="city">Miasto<span className="required">*</span>:</label>
        <input
          id="city"
          type="text"
          value={postalAddress.city || ""}
          onChange={(e) =>
            setPostalAddress((prev) => ({ ...prev, city: e.target.value }))
          }
        />
      </div>

      <div className="field-row">
        <label htmlFor="country">Kraj<span className="required">*</span>:</label>
        <input
          id="country"
          type="text"
          value={postalAddress.country || ""}
          onChange={(e) =>
            setPostalAddress((prev) => ({ ...prev, country: e.target.value }))
          }
        />
      </div>

      <div className="address-popup-buttons">
        <button
          className="confirm"
          onClick={() => {
            setSavedAddress(
              `${postalAddress.street || ""} ${postalAddress.number || ""}${postalAddress.apartment ? "/" + postalAddress.apartment : ""}, ${postalAddress.zip || ""} ${postalAddress.city || ""}, ${postalAddress.country || ""}`
            );
            setShowPopup(false);
          }}
          disabled={
            !postalAddress.street ||
            !postalAddress.number ||
            !postalAddress.zip ||
            !postalAddress.city ||
            !postalAddress.country
          }
        >
          Wyślij na adres domowy
        </button>

        <button
          className="confirm"
          onClick={() => {
            handleDeclarationChange("emailAgreementConsent", true);
            setShowPopup(false);
          }}
        >
          Wyślij na adres email
        </button>
      </div>
    </div>
  </>
)}


    </div>
  );
};

export default Summary;
